import { useMemo, useRef } from 'react'
import Chart from 'react-apexcharts'
import type { ChartCard } from '@/features/dashboard/api/schemas'
import {
  barWidthPx,
  expandedBarModel,
  expandedBarOptions,
} from './lib/expandedBarOptions'
import { chartSummary } from './lib/chartSummary'
import { useChartPalette } from './lib/useChartPalette'
import { useElementSize } from './lib/useElementSize'
import styles from './ExpandedBarChart.module.css'

type ExpandedBarChartProps = {
  card: ChartCard
}

/** The expanded card's column chart: neutral sand bars over dashed gridlines. */
export const ExpandedBarChart = ({ card }: ExpandedBarChartProps) => {
  const slotRef = useRef<HTMLDivElement>(null)
  const { width, height } = useElementSize(slotRef)
  const { theme, palette } = useChartPalette()

  const model = useMemo(() => expandedBarModel(card), [card])
  // Options depend on the bar's px width, not the slot's, so a resize that
  // leaves it unchanged doesn't rebuild them.
  const barPx = barWidthPx(width, model.values.length)
  const options = useMemo(
    () => expandedBarOptions(card, model, palette, barPx),
    [card, model, palette, barPx]
  )
  const series = useMemo(
    () => [{ name: card.name, data: model.values }],
    [card.name, model.values]
  )

  return (
    <div
      ref={slotRef}
      className={styles.slot}
      role='img'
      aria-label={
        model.goal === null
          ? chartSummary(card)
          : `${chartSummary(card)}. ${model.goalText}`
      }
    >
      {width > 0 && height > 0 && (
        <Chart
          // A new theme redraws the chart, so its bars are reshaped.
          key={theme}
          type='bar'
          width={width}
          height={height}
          options={options}
          series={series}
        />
      )}
    </div>
  )
}
